import { getSnowplowTracker } from '@hudl/analytics';
import Tracker from '@hudl/analytics/dist/snowplow/trackers/Tracker';
import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTracking } from '@/analytics/snowplow/hooks/useTracking';
import env from '@/env';

const TrackingProvider = ({ children }: { children: ReactNode }) => {
  useTracking();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  // Configuring the snowplow tracker above is an async operation, but I don't want to make this component async
  // so we just wait for the tracker to be ready before we try to use it.
  let parachuteSnowplowTracker: Tracker | undefined;
  try {
    parachuteSnowplowTracker = getSnowplowTracker('parachute');
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Error getting Snowplow tracker. Possibly not ready yet.', e);
  }

  // Track a unique page view event on every change to page or query params
  useEffect(() => {
    parachuteSnowplowTracker?.trackPageViewEvent(document.title);
  }, [parachuteSnowplowTracker, pathname, search]);

  // Track time on page for every unique page only
  useEffect(() => {
    parachuteSnowplowTracker?.trackPagePings(1, 1); // 1s delay, 1s interval
  }, [parachuteSnowplowTracker, pathname]);

  // we couldn't figure out why page pings were not working until the user initially navigates to parachute.
  // As a workaround, we force the page to auto navigate after 2 seconds which triggers the page pings.
  useEffect(() => {
    const timer = setTimeout(() => {
      // Read pathname from window inside the timer rather than via closure — by 2s in, the
      // locale redirect (RedirectToLocalised) has already navigated us away from '/', and we
      // want to preserve whatever URL the user is actually on rather than kick them back.
      const currentPath = window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('stp', crypto.randomUUID());
      navigate(`${currentPath}?${searchParams}`, { replace: true });
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return children;
};

// prevent snowplow from trying to track the dev environment
export const SnowplowProvider = ({ children }: { children: ReactNode }) =>
  env.VITE_NODE_ENV !== 'development' ? <TrackingProvider>{children}</TrackingProvider> : children;
